// prediction-market-portal/src/onboarding-trading-key.js
export function withTimeout(promise, ms, label = "operation") {
  let timer;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(
      () =>
        reject(
          new Error(
            `${label} timed out after ${Math.round(ms / 1000)}s - unlock the Dusk extension and try again`
          )
        ),
      ms
    );
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}

export function assertWalletCanSign(wallet) {
  if (typeof wallet?.signMessage !== "function") {
    throw new Error(
      "Connected wallet does not support message signing (dusk_signMessage). Update the Dusk browser extension."
    );
  }
  const caps = wallet.state?.capabilities?.features?.signMessage;
  if (caps === false) {
    throw new Error(
      "This wallet reports it cannot sign messages. Update the Dusk browser extension, then reconnect."
    );
  }
}

/**
 * Derive + link. Mirrors participant-panel connectTradingKey safety net:
 * refuse overwrite when linked commitment ≠ derived.
 */
export async function connectTradingKeyFlow({
  wallet,
  deriveTradingKey,
  loadParticipant,
  saveParticipant,
  saveTradingKey,
  linkCommitment,
  loadBackendConfig,
  timeoutMs = 120_000,
}) {
  assertWalletCanSign(wallet);
  const derived = await withTimeout(
    deriveTradingKey(wallet),
    timeoutMs,
    "Wallet signature"
  );
  const current = loadParticipant();
  if (!current?.participant_id || current.status !== "verified") {
    throw new Error("Verify your wallet with a self-transfer before connecting a trading key.");
  }
  if (
    current.trading_commitment_hex &&
    current.trading_commitment_hex.toLowerCase() !==
      derived.commitment_hex.toLowerCase()
  ) {
    throw new Error(
      "This wallet's trading key does not match the commitment already linked to your participant. " +
        "Not overwriting - that would orphan existing positions. " +
        "If you used an old randomly-generated key, restore it via Import backup (Settings), " +
        "or continue with a different Moonlight account."
    );
  }
  if (!current.trading_commitment_hex) {
    const linked = await linkCommitment(
      loadBackendConfig(),
      current.participant_id,
      derived.commitment_hex
    );
    saveTradingKey(derived);
    saveParticipant(linked);
    return { key: derived, participant: linked };
  }
  saveTradingKey(derived);
  return { key: derived, participant: current };
}
